import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { User, Mail, Shield, Key, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { ProfilePictureUploader } from '../components/ProfilePictureUploader';
import { PasswordStrengthHint } from '../components/PasswordStrengthHint';
import { checkPasswordStrength, PASSWORD_POLICY_MESSAGE } from '../utils/passwordStrength';

export const ProfilePage = () => {
  const { user, refreshUser, hasPermission, hasRole } = useAuth();
  // Regular employees can only manage profile picture + password. Privileged
  // users (super admin or anyone with `users.write`) can also edit name/phone.
  // Email always stays read-only — it's the login identifier.
  const canEditProfile = hasRole?.('super_admin') || hasPermission?.('users.write');
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [passwordData, setPasswordData] = useState({ current: '', new_password: '', confirm: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/auth/profile', { first_name: formData.first_name, last_name: formData.last_name, phone: formData.phone });
      toast.success('Profile updated');
    } catch { toast.error('Update failed'); }
  };

  const uploadPicture = async (blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'profile.jpg');
    const res = await api.post('/auth/profile-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (res.data.success) {
      toast.success('Profile picture updated');
      await refreshUser();
    }
  };

  const deletePicture = async () => {
    await api.delete('/auth/profile-picture');
    toast.success('Profile picture removed');
    await refreshUser();
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!checkPasswordStrength(passwordData.new_password).valid) {
      toast.error(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (passwordData.new_password !== passwordData.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await api.put('/auth/password', { current_password: passwordData.current, new_password: passwordData.new_password });
      toast.success('Password changed');
      setPasswordData({ current: '', new_password: '', confirm: '' });
      // Refresh the user record so `must_reset_password` clears and the
      // forced-redirect guard in DashboardLayout lets normal navigation resume.
      if (refreshUser) { await refreshUser(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Password change failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="profile-page">
      {user?.must_reset_password && (
        <div
          className="border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 rounded-md p-4 flex gap-3 items-start"
          data-testid="must-reset-password-banner"
        >
          <Key className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-amber-900 dark:text-amber-200">
              Please change your default password to continue.
            </div>
            <div className="text-amber-800/80 dark:text-amber-200/80 mt-1">
              Your account was migrated from the previous ERP. Your current
              password is your mobile number (with the country code, e.g.
              <code className="mx-1 px-1 rounded bg-amber-100 dark:bg-amber-900/40">
                +919920084694
              </code>). Set a stronger password below to unlock the rest of the system.
            </div>
          </div>
        </div>
      )}
      <div><h1 className="text-2xl font-bold">My Profile</h1><p className="text-muted-foreground">Manage your account details</p></div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><User className="h-5 w-5 text-primary" />Profile Information</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <div className="flex items-center gap-6">
              <ProfilePictureUploader
                picture={user?.profile_picture}
                firstName={user?.first_name}
                lastName={user?.last_name}
                seed={user?.id}
                size="xl"
                onUpload={uploadPicture}
                onDelete={deletePicture}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xl font-bold">{formData.first_name} {formData.last_name}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1"><Mail className="h-4 w-4" />{formData.email}</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1"><Shield className="h-4 w-4" />{user?.roles?.[0]?.name || 'Super Admin'}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="profile-first-name"
                  disabled={!canEditProfile}
                  readOnly={!canEditProfile}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="profile-last-name"
                  disabled={!canEditProfile}
                  readOnly={!canEditProfile}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  data-testid="profile-email"
                  disabled
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!canEditProfile}
                  readOnly={!canEditProfile}
                />
              </div>
            </div>
            {canEditProfile ? (
              <Button type="submit" data-testid="profile-save-btn"><Save className="h-4 w-4 mr-2" />Save Changes</Button>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="profile-readonly-note">
                Name, email and phone are managed by HR. To update them, please reach out to your administrator.
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Key className="h-5 w-5 text-amber-500" />Change Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2"><Label>Current Password</Label><div className="relative"><Input type={showCurrentPw ? 'text' : 'password'} value={passwordData.current} onChange={e => setPasswordData({ ...passwordData, current: e.target.value })} required data-testid="current-password" className="pr-10" /><button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-current-password">{showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>New Password</Label><div className="relative"><Input type={showNewPw ? 'text' : 'password'} value={passwordData.new_password} onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })} required data-testid="new-password" className="pr-10" /><button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-new-password">{showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
              <div className="space-y-2"><Label>Confirm Password</Label><Input type="password" value={passwordData.confirm} onChange={e => setPasswordData({ ...passwordData, confirm: e.target.value })} required data-testid="confirm-password" /></div>
            </div>
            <PasswordStrengthHint password={passwordData.new_password} />
            <Button type="submit" variant="outline" data-testid="change-password-btn"><Key className="h-4 w-4 mr-2" />Change Password</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
